import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import axios from "axios";
import {
  Alert,
  Backdrop,
  Card,
  CircularProgress,
  Grid,
  Snackbar,
  Typography,
} from "@mui/material";

import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import ScenarioViewForm from "./components/ScenarioViewForm";
import ScenarioLocationMap from "./components/ScenarioLocationMap";
import ScenarioForm from "../create/components/ScenarioForm";
import ScenarioMap from "../create/components/scenarioMap/ScenarioMap";
import config from "../../../config";
import scenarioMetadata from "../create/data/scenarioMetadata";
import { fetchEvents } from "../create/functions/fetchEvents";
import { fetchRoutes } from "../create/functions/fetchRoutes";
import { fetchArea } from "../create/functions/fetchArea";
import { fetchNetwork } from "../create/functions/fetchNetwork";
import i18n from "../../../i18n";

import "./ScenarioView.css";

const formatDateTime = (value) => {
  if (!value) return "-";

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;

  const day = String(date.getDate()).padStart(2, "0");
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const year = date.getFullYear();
  const hours = String(date.getHours()).padStart(2, "0");
  const minutes = String(date.getMinutes()).padStart(2, "0");

  return `${day}/${month}/${year} ${hours}:${minutes}`;
};

const joinValues = (values) => {
  if (!Array.isArray(values) || !values.length) return "-";
  return values.join(", ");
};

const getCategoryName = (categoryId) =>
  scenarioMetadata.categories.find((item) => item.id === categoryId)?.name || categoryId || "-";

const getSelectedKpi = (kpiId) => scenarioMetadata.kpis.find((item) => item.id === kpiId) || null;

const getKpiName = (selectedKpi, kpiId) => selectedKpi?.name || kpiId || "-";

const getKpiOptionName = (selectedKpi, optionId) => {
  if (!optionId) return "-";
  return selectedKpi?.options?.find((item) => item.id === optionId)?.label || optionId;
};

const getActionName = (actionId) =>
  scenarioMetadata.actions.find((item) => item.id === actionId)?.name || actionId || "-";

const getTargetNames = (actionId, targetIds) => {
  if (!Array.isArray(targetIds) || !targetIds.length) return "-";
  const action = scenarioMetadata.actions.find((item) => item.id === actionId);
  return targetIds
    .map((targetId) => action?.targets?.find((item) => item.id === targetId)?.name || targetId)
    .join(", ");
};

const normalizeErrorMessage = (err) => {
  const fallback = i18n.t("scenarios.create.submitFailFallback");
  const data = err?.response?.data;

  if (typeof data === "string") return data;

  const common = data?.message || data?.detail || data?.error || data?.title || data?.msg;
  if (typeof common === "string") return common;

  const validationArray = Array.isArray(data) ? data : Array.isArray(data?.detail) ? data.detail : null;

  if (validationArray) {
    return validationArray
      .slice(0, 3)
      .map((entry) => {
        const loc = Array.isArray(entry?.loc) ? entry.loc.join(".") : entry?.loc;
        const msg = entry?.msg || entry?.message || i18n.t("scenarios.create.validationError");
        return loc ? `${loc}: ${msg}` : msg;
      })
      .join(" | ");
  }

  try {
    if (data != null) return JSON.stringify(data);
  } catch {
    return fallback;
  }

  return fallback;
};

const mapMonitoringNames = (monitoringItems, monitoringIds, monitoringType) => {
  if (!Array.isArray(monitoringIds) || !monitoringIds.length) return "-";
  if (!Array.isArray(monitoringItems) || !monitoringItems.length) return joinValues(monitoringIds);

  const idKey =
    monitoringType === "areas" ? "area_id" : monitoringType === "routes" ? "id" : "id";

  return monitoringIds
    .map((monitoringId) => {
      const match = monitoringItems.find((item) => {
        const candidateId = item?.[idKey] ?? item?.id;
        return String(candidateId) === String(monitoringId);
      });
      return match?.name || monitoringId;
    })
    .join(", ");
};

const mapScenarioFromApi = (item, monitoringNames = null) => {
  const selectedKpi = getSelectedKpi(item?.kpi_id);

  return {
    scenario_id: item?.scenario_id ?? "-",
    category: getCategoryName(item?.category_id),
    name: item?.name ?? "-",
    description: item?.description ?? "-",
    start_date: formatDateTime(item?.start_date),
    end_date: formatDateTime(item?.end_date),
    kpi: getKpiName(selectedKpi, item?.kpi_id),
    kpi_option: getKpiOptionName(selectedKpi, item?.kpi_option_id),
    kpi_monitoring_ids: monitoringNames || joinValues(item?.kpi_monitoring_ids),
    location: item?.location ?? null,
    location_type: item?.location?.type ?? "-",
    location_path_ids: joinValues(item?.location_path_ids),
    action: getActionName(item?.action_id),
    targets: getTargetNames(item?.action_id, item?.target_ids),
    type_ids: item?.type_ids ?? "-",
    message: item?.message || "-",
    created_by: item?.created_by ?? "-",
  };
};

const mapScenarioToEditModel = (item) => ({
  category_id: item?.category_id ?? null,
  name: item?.name ?? "",
  description: item?.description ?? "",
  start_date: item?.start_date ?? null,
  end_date: item?.end_date ?? null,
  kpi_id: item?.kpi_id ?? null,
  kpi_option_id: item?.kpi_option_id ?? null,
  kpi_monitoring_ids: item?.kpi_monitoring_ids ?? [],
  type_ids: item?.type_ids ?? null,
  location: item?.location ?? null,
  location_path_ids: item?.location_path_ids ?? [],
  action_id: item?.action_id ?? 1,
  target_ids: item?.target_ids ?? [],
  message: item?.message ?? "",
  created_by: item?.created_by ?? "anonymous",
});

function ScenarioView() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id } = useParams();
  const SCENARIOS_API_URL = config.SYNCHROEU_URL || "https://toolbox.synchromode.eu";

  const [scenarioData, setScenarioData] = useState(null);
  const [scenarioRaw, setScenarioRaw] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editing, setEditing] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [network, setNetwork] = useState(null);
  const [loadingNetwork, setLoadingNetwork] = useState(true);
  const [monitoringItems, setMonitoringItems] = useState({ type: null, items: [] });
  const [editData, setEditData] = useState({
    category_id: null,
    name: "",
    description: "",
    start_date: null,
    end_date: null,
    kpi_id: null,
    kpi_option_id: null,
    kpi_monitoring_ids: [],
    type_ids: null,
    location: null,
    location_path_ids: [],
    action_id: 1,
    target_ids: [],
    message: "",
    created_by: "anonymous",
  });
  const [alertState, setAlertState] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  useEffect(() => {
    let cancelled = false;

    async function fetchScenario() {
      try {
        setLoading(true);
        setError(null);
        const response = await axios.get(`${SCENARIOS_API_URL}/scenarios/${id}`, {
          headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
          },
        });

        if (!cancelled) {
          setScenarioRaw(response.data);
          setScenarioData(mapScenarioFromApi(response.data));
          setEditData(mapScenarioToEditModel(response.data));
        }
      } catch (fetchError) {
        if (!cancelled) {
          setError(t("scenarios.view.loadError"));
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    fetchScenario();
    return () => {
      cancelled = true;
    };
  }, [SCENARIOS_API_URL, id]);

  useEffect(() => {
    let cancelled = false;

    async function loadNetwork() {
      try {
        const data = await fetchNetwork();
        if (!cancelled) setNetwork(data);
      } finally {
        if (!cancelled) setLoadingNetwork(false);
      }
    }

    loadNetwork();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;

    async function resolveMonitoringNames() {
      if (!scenarioRaw?.kpi_id || !Array.isArray(scenarioRaw?.kpi_monitoring_ids)) return;

      const selectedKpi = getSelectedKpi(scenarioRaw.kpi_id);
      const monitoringType = selectedKpi?.monitoring?.type;

      if (!monitoringType) return;

      try {
        let monitoringItems = [];

        if (monitoringType === "events") {
          monitoringItems = await fetchEvents();
        } else if (monitoringType === "routes") {
          monitoringItems = await fetchRoutes();
        } else if (monitoringType === "areas") {
          monitoringItems = await fetchArea();
        }

        if (cancelled) return;

        const monitoringNames = mapMonitoringNames(
          monitoringItems,
          scenarioRaw.kpi_monitoring_ids,
          monitoringType
        );

        setScenarioData(mapScenarioFromApi(scenarioRaw, monitoringNames));
      } catch (monitoringError) {
        if (!cancelled) {
          setScenarioData(mapScenarioFromApi(scenarioRaw));
        }
      }
    }

    resolveMonitoringNames();
    return () => {
      cancelled = true;
    };
  }, [scenarioRaw]);

  const handleScenarioChange = (patch) => {
    setEditData((prev) => ({ ...prev, ...patch }));
  };

  const handleEdit = () => {
    if (scenarioRaw) {
      setEditData(mapScenarioToEditModel(scenarioRaw));
    }
    setEditing(true);
  };

  const handleCancelEdit = () => {
    if (scenarioRaw) {
      setEditData(mapScenarioToEditModel(scenarioRaw));
    }
    setEditing(false);
  };

  const handleUpdate = async (payload) => {
    if (submitting) return;

    const requestPayload = {
      category_id: payload.category_id,
      name: payload.name,
      description: payload.description,
      start_date: payload.start_date,
      end_date: payload.end_date,
      kpi_id: payload.kpi_id,
      kpi_option_id: payload.kpi_option_id,
      kpi_monitoring_ids: payload.kpi_monitoring_ids,
      location: payload.location,
      location_path_ids: payload.location_path_ids,
      action_id: payload.action_id,
      target_ids: payload.target_ids,
      type_ids: payload.type_ids,
      message: payload.message,
      created_by: scenarioRaw?.created_by || payload.created_by || "anonymous",
    };

    try {
      setSubmitting(true);

      await axios.put(`${SCENARIOS_API_URL}/scenarios/${id}`, requestPayload, {
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
        },
      });

      const refreshedScenario = { ...scenarioRaw, ...requestPayload, scenario_id: scenarioRaw?.scenario_id ?? id };
      setScenarioRaw(refreshedScenario);
      setScenarioData(mapScenarioFromApi(refreshedScenario));
      setEditData(mapScenarioToEditModel(refreshedScenario));
      setEditing(false);
      setAlertState({
        open: true,
        message: t("scenarios.view.updateSuccess"),
        severity: "success",
      });
    } catch (submitError) {
      setAlertState({
        open: true,
        message: normalizeErrorMessage(submitError),
        severity: "error",
      });
    } finally {
      setSubmitting(false);
    }
  };

  if (loading || loadingNetwork) {
    return (
      <DashboardLayout>
        <div className="scenario-view-spinner">
          <CircularProgress />
        </div>
      </DashboardLayout>
    );
  }

  if (error) {
    return (
      <DashboardLayout>
        <div className="scenario-view-spinner">
          <Typography color="error">{error}</Typography>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <Snackbar
        open={alertState.open}
        autoHideDuration={5000}
        onClose={() => setAlertState((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          severity={alertState.severity}
          variant="filled"
          onClose={() => setAlertState((s) => ({ ...s, open: false }))}
        >
          {String(alertState.message)}
        </Alert>
      </Snackbar>

      <Backdrop
        open={submitting}
        sx={{ zIndex: (theme) => theme.zIndex.drawer + 2000 }}
      >
        <Grid container direction="column" alignItems="center" gap={2}>
          <CircularProgress />
          <Typography variant="body2">{t("scenarios.view.updatingScenario")}</Typography>
        </Grid>
      </Backdrop>

      <Card id="scenario-view">
        <Grid container spacing={2} sx={{ width: "100%", m: 0, p: 0 }}>
          <Grid item xs={12} md={4} lg={3}>
            {editing ? (
              <>
                <ScenarioForm
                  value={editData}
                  onChange={handleScenarioChange}
                  metadata={scenarioMetadata}
                  onSubmit={handleUpdate}
                  onMonitoringItemsChange={setMonitoringItems}
                />
                <Grid container px={3} pb={3}>
                  <Grid item xs={12}>
                    <Typography
                      component="button"
                      onClick={handleCancelEdit}
                      style={{
                        background: "transparent",
                        border: "none",
                        color: "#4fc3f7",
                        cursor: "pointer",
                        padding: 0,
                        marginTop: "-16px",
                      }}
                    >
                      {t("scenarios.view.cancelEdit")}
                    </Typography>
                  </Grid>
                </Grid>
              </>
            ) : (
              <ScenarioViewForm
                value={scenarioData}
                onBack={() => navigate("/scenariosLibrary")}
                onEdit={handleEdit}
              />
            )}
          </Grid>

          <Grid item xs={12} md={8} lg={9} sx={{ p: 0 }}>
            {editing ? (
              <ScenarioMap
                value={editData}
                onChange={handleScenarioChange}
                network={network}
                monitoringItems={monitoringItems}
              />
            ) : (
              <ScenarioLocationMap location={scenarioData?.location} />
            )}
          </Grid>
        </Grid>
      </Card>
    </DashboardLayout>
  );
}

export default ScenarioView;
